import React, { useState, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Eye,
  Facebook,
  Share2,
  Download,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { isMobileDevice, logger } from '../../lib/utils';
import { isProfane, sanitizeName } from '../../lib/profanityFilter';
import { toast } from 'sonner';
import {
  API,
  getFacebookShareUrl,
  getChallengePreviewImageUrl,
  handleFacebookShare,
} from './challengeUtils';

/**
 * Shared share-actions block used on the "Score Saved!" screens
 * (regular + daily puzzle completion) to replace the legacy
 * "Challenge Friends" multi-step modal.
 *
 * The challenge is created lazily — on the first action the user takes
 * (View / Facebook / Other Social Media). After it's created we cache
 * the code so subsequent buttons don't re-create it.
 *
 * Props mirror the inputs `useChallengeCreation` previously took:
 *   - imageUrl, difficulty, scoreData, time, moves, deviceId, playerName
 *   - pushNotifications (optional, for silent auto-subscribe)
 *   - existingChallengeCode (optional) — if provided, skip the lazy-create
 *     flow and use this code directly. Used by the "Mine" tab's
 *     "Share Challenge" view to share an already-created challenge.
 */
export function ChallengeShareActions({
  imageUrl,
  difficulty,
  scoreData,
  time,
  moves,
  deviceId,
  playerName,
  pushNotifications,
  existingChallengeCode,
}) {
  const [challengeCode, setChallengeCode] = useState(existingChallengeCode || null);
  const [scorecardKey, setScorecardKey] = useState(existingChallengeCode ? Date.now() : 0);
  const [pendingAction, setPendingAction] = useState(null); // 'view' | 'facebook' | 'other' | null
  const [showPreview, setShowPreview] = useState(false);
  const [showOtherShare, setShowOtherShare] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [previewError, setPreviewError] = useState(false);

  // Track in-flight creation so multiple clicks don't fire duplicate POSTs
  const inflightRef = useRef(null);

  const ensureChallenge = useCallback(async () => {
    if (challengeCode) return challengeCode;
    if (existingChallengeCode) return existingChallengeCode;
    if (inflightRef.current) return inflightRef.current;

    const rawName = playerName || localStorage.getItem('jigsaw_player_name') || 'Player';
    const name = sanitizeName(rawName) || 'Player';
    if (isProfane(name.trim())) {
      toast.error('Profanity Detected! - Try Again');
      return null;
    }

    const promise = (async () => {
      try {
        localStorage.setItem('jigsaw_player_name', name.trim());
        const isMobile = isMobileDevice();
        const response = await axios.post(`${API}/challenges?is_mobile=${isMobile}`, {
          image_url: imageUrl,
          difficulty,
          creator_name: name.trim(),
          creator_device_id: deviceId,
        });
        const newCode = response.data.challenge_code;

        if (scoreData && time !== undefined && moves !== undefined) {
          try {
            await axios.post(`${API}/challenges/${newCode}/score?is_mobile=${isMobile}`, {
              player_name: name.trim(),
              time_seconds: time,
              moves,
              device_id: deviceId,
            });
            try {
              await axios.post(
                `${API}/challenges/${newCode}/regenerate-scorecard?is_mobile=${isMobile}`
              );
            } catch (e) {
              logger.error('Failed to regenerate scorecard:', e);
            }
          } catch (e) {
            logger.error('Failed to submit creator score:', e);
          }
        }

        const saved = JSON.parse(localStorage.getItem('jigsaw_my_challenges') || '[]');
        if (!saved.includes(newCode)) {
          saved.unshift(newCode);
          localStorage.setItem('jigsaw_my_challenges', JSON.stringify(saved.slice(0, 20)));
        }

        if (
          pushNotifications?.isSupported &&
          !pushNotifications?.isSubscribed &&
          pushNotifications?.subscribe
        ) {
          pushNotifications.subscribe().catch(() => {
            // Silent fail — notifications are optional
          });
        }

        setChallengeCode(newCode);
        setScorecardKey(Date.now());
        return newCode;
      } catch (error) {
        logger.error('Failed to create challenge:', error);
        toast.error('Could not create challenge. Please try again.');
        return null;
      } finally {
        inflightRef.current = null;
      }
    })();

    inflightRef.current = promise;
    return promise;
  }, [
    challengeCode,
    existingChallengeCode,
    playerName,
    imageUrl,
    difficulty,
    scoreData,
    time,
    moves,
    deviceId,
    pushNotifications,
  ]);

  const runWithChallenge = useCallback(
    async (action, fn) => {
      setPendingAction(action);
      try {
        const code = await ensureChallenge();
        if (code) await fn(code);
      } finally {
        setPendingAction(null);
      }
    },
    [ensureChallenge]
  );

  const onView = () =>
    runWithChallenge('view', () => {
      setPreviewError(false);
      setShowPreview(true);
    });

  const onFacebook = () =>
    runWithChallenge('facebook', (code) => {
      handleFacebookShare(getFacebookShareUrl(code));
    });

  const onOther = () =>
    runWithChallenge('other', () => {
      setShowOtherShare(true);
    });

  const onDownloadScorecard = async () => {
    if (!challengeCode) return;
    setIsDownloading(true);
    try {
      const url = getChallengePreviewImageUrl(challengeCode);
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `challenge-${challengeCode}-scorecard.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      setShowOtherShare(false);
    } catch (error) {
      logger.error('Failed to download scorecard:', error);
      toast.error('Failed to download score card.');
    } finally {
      setIsDownloading(false);
    }
  };

  const previewSrc = challengeCode
    ? `${getChallengePreviewImageUrl(challengeCode)}?t=${scorecardKey}`
    : null;

  return (
    <>
      <div className="space-y-2 sm:space-y-3">
        {/* View your score card */}
        <Button
          variant="outline"
          className="w-full h-10 sm:h-12 justify-center text-sm sm:text-base"
          onClick={onView}
          disabled={pendingAction !== null}
          data-testid="view-scorecard-btn"
        >
          {pendingAction === 'view' ? (
            <Loader2 className="h-4 w-4 sm:h-5 sm:w-5 mr-2 animate-spin flex-shrink-0" />
          ) : (
            <Eye className="h-4 w-4 sm:h-5 sm:w-5 mr-2 flex-shrink-0" />
          )}
          View your score card
        </Button>

        {/* Share on Facebook */}
        <Button
          className="w-full h-10 sm:h-12 justify-center text-sm sm:text-base bg-[#1877F2] hover:bg-[#166FE5] text-white"
          onClick={onFacebook}
          disabled={pendingAction !== null}
          data-testid="share-on-facebook-btn"
        >
          {pendingAction === 'facebook' ? (
            <Loader2 className="h-4 w-4 sm:h-5 sm:w-5 mr-2 animate-spin flex-shrink-0" />
          ) : (
            <Facebook className="h-4 w-4 sm:h-5 sm:w-5 mr-2 flex-shrink-0" />
          )}
          Share on Facebook
        </Button>

        {/* Share on other Social Media */}
        <Button
          variant="outline"
          className="w-full h-10 sm:h-12 justify-center text-sm sm:text-base"
          onClick={onOther}
          disabled={pendingAction !== null}
          data-testid="share-on-other-social-btn"
        >
          {pendingAction === 'other' ? (
            <Loader2 className="h-4 w-4 sm:h-5 sm:w-5 mr-2 animate-spin flex-shrink-0" />
          ) : (
            <Share2 className="h-4 w-4 sm:h-5 sm:w-5 mr-2 flex-shrink-0" />
          )}
          Share on other Social Media
        </Button>
      </div>

      {/* Scorecard preview dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent
          className="sm:max-w-lg w-[calc(100vw-1rem)] max-w-[calc(100vw-1rem)]"
          data-testid="scorecard-preview-dialog"
        >
          <DialogHeader>
            <DialogTitle>Your Score Card</DialogTitle>
            {challengeCode && (
              <DialogDescription>
                Challenge code:{' '}
                <span className="font-mono font-semibold text-primary">{challengeCode}</span>
              </DialogDescription>
            )}
          </DialogHeader>
          <div className="relative rounded-lg overflow-hidden bg-muted/30 border min-h-[160px]">
            {previewError ? (
              <div className="flex flex-col items-center justify-center h-[160px] text-muted-foreground p-4">
                <AlertCircle className="h-8 w-8 mb-2" />
                <span className="text-sm text-center">Score card preview unavailable</span>
              </div>
            ) : previewSrc ? (
              <img
                key={scorecardKey}
                src={previewSrc}
                alt="Challenge Score Card"
                className="w-full h-auto"
                data-testid="scorecard-preview-image"
                onError={() => setPreviewError(true)}
              />
            ) : null}
          </div>
          <DialogFooter className="flex-row justify-end gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => setShowPreview(false)}
              data-testid="scorecard-preview-close-btn"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Other social media instructions dialog */}
      <Dialog open={showOtherShare} onOpenChange={setShowOtherShare}>
        <DialogContent
          className="sm:max-w-md w-[calc(100vw-1rem)] max-w-[calc(100vw-1rem)] sm:w-auto"
          data-testid="other-share-instructions-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="h-5 w-5" />
              Share Your Challenge
            </DialogTitle>
            <DialogDescription className="pt-2 space-y-2 text-sm">
              <span className="block">To share your challenge on other platforms:</span>
              <span className="block">
                1. Tap <span className="font-semibold">Download</span> below to save your
                challenge scorecard.
              </span>
              <span className="block">
                2. Open the downloaded image from your device.
              </span>
              <span className="block">
                3. Tap <span className="font-semibold">Share</span> in your image viewer to
                send it to any app.
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-row justify-end gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => setShowOtherShare(false)}
              disabled={isDownloading}
              data-testid="other-share-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={onDownloadScorecard}
              disabled={isDownloading || !challengeCode}
              data-testid="other-share-download-btn"
            >
              {isDownloading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2 flex-shrink-0" />
              )}
              Download
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
